import { checkFeature } from '@bybeam/platform-lib/features/check';
import { FeatureName } from '@bybeam/platform-types';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import Loading from '@platform-ui-common/components/Loading/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { StorageKeys } from '@platform-ui/@types/storage.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import Button from '@platform-ui/common/components/Button/index.js';
import { useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';

function BeamSignIn(): JSX.Element {
  const partner = usePartner();
  const allowBeamEmployeeSignin = checkFeature(
    partner.features,
    FeatureName.AuthenticationBeamEmployees,
  );

  const userHome = useUserHomeRoute();
  const samlRedirect = localStorage.getItem(StorageKeys.SAMLRedirect) === 'true';

  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const {
    user,
    loading,
    identity: {
      auth,
      signinMethods: { beamEmployeeSAML },
    },
  } = useAuth();

  useEffect(() => {
    if (samlRedirect) setAuthLoading(true);
    if (auth?.currentUser && user) navigate(userHome);
  }, [samlRedirect, auth?.currentUser, user, navigate, userHome]);

  const [authLoading, setAuthLoading] = useState(false);

  if (!allowBeamEmployeeSignin) return <Redirect to={userHome} />;

  // This is where we'll check for "seats" when implemented
  if (samlRedirect) return <Loading size="XXL" fullPage />;

  return (
    <div className="relative w-full h-full flex-grow flex flex-col items-center">
      <Typography variant="h1" alignment="center">
        Sign in using your Beam Okta account
      </Typography>
      <div className="flex flex-col gap-10">
        <div className="mt-8 w-2/3 self-center">
          <Message variant="info">
            Currently there is no support for clicking into the <b>Beam</b> app from Okta. Signing
            in through this page is recommended. If you are having trouble, refer to the following
            helpers:
            <ul className="list-disc mt-6 ml-8 flex flex-col gap-4">
              <li>
                Ensure the <b>Beam Platform</b> app is available in your Okta dashboard
              </li>
              <li>
                <Markdown>
                  If you do not see it, message
                  [#eng-all](https://bybeam.slack.com/archives/C05R8BCT0AJ) to be added
                </Markdown>
              </li>
              <li>
                Clicking <b>Sign In</b> below will redirect you to sign in to Okta
              </li>
              <li>Once sign in is complete, you will be redirected back to the platform</li>
              <li>
                <b>Authentication: Beam Employees</b> feature must be enabled on the partner
              </li>
            </ul>
          </Message>
        </div>
        <div className="w-full flex flex-col gap-y-8 items-center text-center">
          <ErrorDisplay errors={error} visible={!!error} message="Error signing in" />
          <Button loading={loading || authLoading} variant="outline" onClick={beamEmployeeSAML}>
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}

export default BeamSignIn;
